import { exteriorMasonrySlotNames } from "building-types/materials/exteriorMaterialSlotNames";
import type { ExteriorPalette } from "building-types/materials/exteriorPaletteTypes";
import type { InteriorPalette } from "building-types/materials/interiorPaletteTypes";
import { PaletteType } from "building-types/materials/paletteType";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import { exteriorPaletteIds, exteriorPaletteNamesById } from "common/constants/palette/builtInPaletteIndex";
import { MasonryIdByName } from "common/materials/library/masonryIdByName";
import { materialSrcFromCacheKey } from "common/materials/materialSrc";
import { fetchJsonAsset } from "common/utils/assetFetcher";
import interiorPaletteLibrary from "doc/settings/interiorMaterialSettingsLibrary";

export const builtInPaletteJsonMasonryPrefix = "_M.";
const masonryIdPrefixLength = builtInPaletteJsonMasonryPrefix.length;

const paletteCache: { [paletteId: string]: Promise<ExteriorPalette> } = {};

export function isBuiltInPaletteJsonMasonryId(materialJsonId: string): boolean {
  return materialJsonId.startsWith(builtInPaletteJsonMasonryPrefix);
}

export function getBuiltInPaletteJsonMasonryId(materialJsonId: string): number {
  return MasonryIdByName[materialJsonId.substring(masonryIdPrefixLength)];
}

export async function getExteriorPalette(
  id: string,
  issues: BuildingGenerationIssue[] = [],
): Promise<ExteriorPalette | undefined> {
  if (paletteCache[id] !== undefined) {
    return paletteCache[id];
  }

  if (!exteriorPaletteIds.includes(id)) {
    return undefined;
  }

  return (paletteCache[id] = fetchJsonAsset("/static/palettes/" + id + ".json", false).then((res) => {
    const palette: Omit<ExteriorPalette, "sortIndex"> = {
      type: PaletteType.EXTERIOR_PALETTE,
      entityType: "PALETTE",
      id,
      name: exteriorPaletteNamesById[id],
    };

    if (!res) {
      issues.push({
        type: BuildingGenerationIssueType.FETCH_ISSUE,
        message: "Palette fetch took too long",
        handle: { type: "PALETTE", id },
      });
    } else {
      const json = JSON.parse(res);

      for (const key of Object.keys(json)) {
        const materialPathOrMasonryId: string = json[key];
        if (isBuiltInPaletteJsonMasonryId(materialPathOrMasonryId)) {
          if (!(exteriorMasonrySlotNames as string[]).includes(key)) {
            console.error("Exterior palette slot linked to masonry, even though it's not a masonry slot!");
          }
          palette[key] = getBuiltInPaletteJsonMasonryId(materialPathOrMasonryId);
        } else {
          palette[key] = materialSrcFromCacheKey(materialPathOrMasonryId);
        }
      }
    }

    return palette as ExteriorPalette;
  }));
}

async function getInteriorPalette(id: string): Promise<InteriorPalette> {
  return interiorPaletteLibrary[id];
}

interface PaletteLibraryAsync {
  ids: string[];
  namesById: { [id: string]: string };
}

export interface ExteriorPaletteLibraryAsync extends PaletteLibraryAsync {
  getPalette: (id: string) => Promise<ExteriorPalette | undefined>;
}

export interface InteriorPaletteLibraryAsync extends PaletteLibraryAsync {
  getPalette: (id: string) => Promise<InteriorPalette>;
}

const interiorPaletteIds: string[] = Object.keys(interiorPaletteLibrary).sort((a, b) =>
  interiorPaletteLibrary[a].name.localeCompare(interiorPaletteLibrary[b].name),
);

const interiorPaletteNamesById = {};

for (const paletteId of interiorPaletteIds) {
  interiorPaletteNamesById[paletteId] = interiorPaletteLibrary[paletteId].name;
}

export const exteriorPaletteLibraryAsync: ExteriorPaletteLibraryAsync = {
  ids: exteriorPaletteIds,
  namesById: exteriorPaletteNamesById,
  getPalette: getExteriorPalette,
};

export const interiorPaletteLibraryAsync: InteriorPaletteLibraryAsync = {
  ids: interiorPaletteIds,
  namesById: interiorPaletteNamesById,
  getPalette: getInteriorPalette,
};
